import { get } from "svelte/store";
import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
import StagedRolloutStore from "@shared/svelte/Stores/StagedRolloutStore";
import type { CartOrderType } from "@shared/types/Cart";
import type { Distributor } from "@shared/types/Distributor";

type CutoffDetails = { cutoffTime: string; cutoffDay: string };

export function isAddOnOrder(order: CartOrderType): boolean {
  if (shouldCheckOrderMinimumRequirements(order)) {
    return order.distributor.reasons.has_reached_daily_order_minimum;
  } else return null;
}

export function meetsOrderMinimum(order: CartOrderType): boolean {
  if (order.distributor.tabc_delivery_minimum_order_blocking) {
    return !orderDoesNotMeetTabcSpiritDeliveryMin(order);
  } else if (!!order.distributor.minimum_dollars_per_order) {
    return !orderIsBelowDollarMinimum(order);
  } else return null;
}

export function nextCutoffDayAndTime(order: CartOrderType): CutoffDetails {
  const cutoffDayAndTime = { cutoffDay: null, cutoffTime: null };
  const nextCutoffDate = findNextCutoffDate(order);
  const today = new Date();

  if (!nextCutoffDate) return cutoffDayAndTime;

  cutoffDayAndTime.cutoffTime = nextCutoffDate
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    })
    .toLocaleLowerCase()
    .replace(" ", "");

  switch (nextCutoffDate.getDate() - today.getDate()) {
    case 0:
      cutoffDayAndTime.cutoffDay = "today";
      break;
    case 1:
      cutoffDayAndTime.cutoffDay = "tomorrow";
      break;
    default:
      cutoffDayAndTime.cutoffDay = nextCutoffDate.toLocaleDateString(undefined, {
        weekday: "long",
      });
  }

  return cutoffDayAndTime;
}

export function orderDoesNotMeetTabcSpiritDeliveryMin(order: CartOrderType): boolean {
  return order.tabc_spirit_delivery_remaining > 0;
}

export function orderIsBelowDollarMinimum(order: CartOrderType): boolean {
  if (!order.distributor.minimum_dollars_per_order || !(order.total > 0)) return false;

  return order.total < order.distributor.minimum_dollars_per_order;
}

export function shouldCheckOrderMinimumRequirements(order: CartOrderType): boolean {
  const hasBlockBelowMinimumDistributorOrdersRollout =
    get(StagedRolloutStore)?.["marketplace__block_below_minimum_distributor_orders"];

  return (
    hasBlockBelowMinimumDistributorOrdersRollout &&
    order.distributor.disable_below_order_minimum_submit
  );
}

function findNextCutoffDate(order: CartOrderType): Date {
  if (order.next_cutoff) return new Date(order.next_cutoff);
  else {
    const distributor: Distributor = get(DistributorsStore).find(
      d => d.id === order.distributor.id,
    );

    if (distributor?.next_cutoff) return new Date(distributor.next_cutoff);
  }
}
